import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { logPageView, logClick } from "../utils/analyticsLogger";

export function usePageViewTracking() {
  const location = useLocation();
  const previousPath = useRef<string>("");

  useEffect(() => {
    if (location.pathname !== previousPath.current) {
      const pageName = getPageNameFromPath(location.pathname);
      logPageView(pageName, location.pathname, {
        search: location.search,
        hash: location.hash,
      });
      previousPath.current = location.pathname;
    }
  }, [location]);
}

function getPageNameFromPath(path: string): string {
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return "Home";

  const pageMap: Record<string, string> = {
    "tickets": "Tickets",
    "dashboard": "Dashboard",
    "knowledge-base": "Knowledge Base",
    "kb": "Knowledge Base",
    "settings": "Settings",
    "agents": "Agents",
    "customers": "Customers",
    "reports": "Reports",
    "service-catalog": "Service Catalog",
    "automations": "Automations",
    "views": "Views",
    "macros": "Macros",
    "triggers": "Triggers",
    "sla": "SLA Policies",
    "business-hours": "Business Hours",
    "groups": "Groups",
    "roles": "Roles",
    "brands": "Brands",
    "channels": "Channels",
    "email": "Email Settings",
    "integrations": "Integrations",
    "security": "Security",
    "audit": "Audit Logs",
  };

  for (const segment of segments) {
    if (pageMap[segment]) {
      return pageMap[segment];
    }
  }

  return segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
}

export function useGlobalClickTracking() {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const clickableElement = findClickableParent(target);

      if (clickableElement) {
        const elementInfo = getElementInfo(clickableElement);
        logClick(elementInfo.name, elementInfo.type, {
          tagName: clickableElement.tagName,
          className: clickableElement.className,
          path: window.location.pathname,
        });
      } else if (target.tagName) {
        const elementInfo = getElementInfo(target);
        logClick(elementInfo.name, "generic_click", {
          tagName: target.tagName,
          className: target.className,
          path: window.location.pathname,
        });
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);
}

function findClickableParent(element: HTMLElement | null): HTMLElement | null {
  while (element) {
    const tagName = element.tagName.toLowerCase();
    if (
      tagName === "button" ||
      tagName === "a" ||
      element.getAttribute("role") === "button" ||
      element.getAttribute("role") === "tab" ||
      element.getAttribute("role") === "menuitem" ||
      element.getAttribute("role") === "option" ||
      element.classList.contains("fui-Button") ||
      element.classList.contains("fui-Tab") ||
      element.classList.contains("fui-MenuItem") ||
      element.hasAttribute("data-track-click")
    ) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
}

function getElementInfo(element: HTMLElement): { name: string; type: string; shouldLog: boolean } {
  const tagName = element.tagName.toLowerCase();
  const role = element.getAttribute("role");
  const ariaLabel = element.getAttribute("aria-label");
  const title = element.getAttribute("title");
  const dataTrackName = element.getAttribute("data-track-name");
  const dataTestId = element.getAttribute("data-testid");
  const id = element.id;
  const textContent = element.textContent?.trim().slice(0, 50);

  const primaryName = dataTrackName || ariaLabel || title || textContent;
  const fallbackName =
    dataTestId ||
    id ||
    `${tagName}${element.className ? "." + element.className.split(" ")[0] : ""}`;
  const name = primaryName || fallbackName || "anonymous_element";

  let type = "unknown";
  if (tagName === "button" || role === "button" || element.classList.contains("fui-Button")) {
    type = "button";
  } else if (tagName === "a") {
    type = "link";
  } else if (role === "tab" || element.classList.contains("fui-Tab")) {
    type = "tab";
  } else if (role === "menuitem" || element.classList.contains("fui-MenuItem")) {
    type = "menu_item";
  } else if (role === "option") {
    type = "option";
  }

  return { name, type, shouldLog: true };
}
